import { useCallback, useEffect, useState } from "react";
import { getJson, postJson, AuthError } from "../lib/auth";
import { Button, Card, Collapsible, Spinner, ErrorBanner, Badge } from "../components";
import { theme, fontStack, radius } from "../theme";
import { StatusPath, buildPath, isTerminalStatus } from "../ui/StatusPath";
import { Transcript, type TranscriptEntry, type TranscriptMedia } from "../ui/Transcript";
import {
  allowedReportTransitions,
  reportProductStatus,
  type ReportProductStatus,
  type ReportType,
} from "@livemessage/protocol";

/**
 * Le DOSSIER DE MODÉRATION — écran de travail principal de la console.
 *
 * Refonte v13.11 (demande d'Henri) : cet écran est utilisé par des modérateurs
 * qui ne sont PAS informaticiens, sur un navigateur de bureau. Trois principes,
 * qui expliquent tous les choix ci-dessous :
 *
 *  1. ON NE MONTRE AUCUN JARGON. Les statuts techniques (ACTIONED, DISMISSED…)
 *     et les `kind` d'événements ne sortent jamais à l'écran : ils sont traduits
 *     en français courant. La machine à états (`allowedReportTransitions`) reste
 *     pourtant la SEULE autorité sur ce qui est permis — on traduit, on ne
 *     réinvente pas.
 *  2. L'AVANCEMENT SE LIT EN HAUT, d'un coup d'œil (chemin en chevrons), et se
 *     pilote par deux boutons : étape suivante / étape précédente.
 *  3. LES PREUVES SE LISENT COMME UNE CONVERSATION (bulles horodatées), pas
 *     comme un dump technique.
 *
 * Disposition (desktop) :
 *   ┌ bandeau : chemin d'avancement + étape précédente / suivante ┐
 *   │ CENTRE                          │ DROITE                    │
 *   │  Message de l'utilisateur       │  Contacter l'utilisateur  │
 *   │  Historique du dossier          │  Coffre de preuves        │
 *   │  Conversation signalée          │  Sanction                 │
 *   │  Note interne                   │                           │
 */

interface DeviceRow {
  deviceId: string;
  createdAt?: string;
  /** MOD-3 — "web" | "ios" | "android", ou null pour un appareil connu avant. */
  platform?: string | null;
}

/**
 * Nomme l'appareil dans la langue du modérateur, et DIT ce que le blocage vaut :
 * un navigateur se change en dix secondes, un téléphone beaucoup moins.
 */
function deviceLabel(platform?: string | null): { name: string; note: string } {
  switch (platform) {
    case "web":
      return { name: "Navigateur", note: "peut être contourné facilement (autre navigateur)" };
    case "ios":
      return { name: "iPhone / iPad", note: "blocage plus solide" };
    case "android":
      return { name: "Téléphone Android", note: "blocage plus solide" };
    default:
      return { name: "Appareil inconnu", note: "nature connue à sa prochaine connexion" };
  }
}
interface ReportedUser {
  id: string;
  displayName?: string | null;
  email?: string | null;
  isBanned?: boolean;
  bannedAt?: string | null;
  banReason?: string | null;
  devices?: DeviceRow[];
}
interface GroupParticipant {
  userId: string;
  role?: string | null;
  user?: { displayName?: string | null; isBanned?: boolean } | null;
}
interface ReportEvent {
  id?: string;
  kind?: string;
  publicReason?: string | null;
  reporterReason?: string | null;
  internalNote?: string | null;
  createdAt?: string;
  actor?: { id?: string; displayName?: string | null } | null;
}
interface Vault {
  contextKind?: string | null;
  turnCount?: number | null;
  capturedAt?: string | null;
  purgeAfter?: string | null;
  entries?: TranscriptEntry[];
  media?: TranscriptMedia[];
  reporterMeta?: { ip?: string | null; platform?: string | null; deviceModel?: string | null } | null;
}
interface Dossier {
  id: string;
  reference?: string | null;
  type?: string;
  status?: string;
  productStatus?: string;
  reason?: string | null;
  sourceChannel?: string | null;
  /** MOD-1 — le déclarant peut-il répondre ? */
  replyOpen?: boolean;
  createdAt?: string;
  reporterId?: string | null;
  reporter?: { id: string; displayName?: string | null; email?: string | null } | null;
  reportedUserId?: string | null;
  reportedUser?: ReportedUser | null;
  group?: { id: string; name?: string | null; participants?: GroupParticipant[] } | null;
  events?: ReportEvent[];
  hasVault?: boolean;
  vault?: { capturedAt?: string | null; purgeAfter?: string | null } | null;
  vaultAccesses?: Array<{ at?: string; adminName?: string | null }>;
  targetReportCount?: number;
  reporterReportCount?: number;
}

/**
 * Traduction des décisions en langage de modérateur. `reportDecisionLabel` du
 * protocole parle « Go / No-Go » : c'est le vocabulaire de la spec, pas celui
 * d'une personne qui découvre l'outil. On garde donc une table d'affichage ICI,
 * avec une phrase d'aide — le bouton doit dire ce qu'il FAIT et ce qu'il
 * PROVOQUE.
 */
function actionCopy(kind: string): { label: string; help: string; danger?: boolean } {
  switch (kind) {
    case "IN_REVIEW":
      return { label: "Commencer l'analyse", help: "Vous prenez le dossier en charge. L'utilisateur voit que son signalement est examiné." };
    case "NEEDS_INFO":
      return { label: "Demander un complément", help: "Il vous manque des informations : l'utilisateur est invité à en dire plus." };
    case "ACTIONED":
      return { label: "Traiter sans sanctionner le compte", help: "Vous avez agi (contenu retiré, correction…) sans sanction visée sur le compte." };
    case "WARNED":
      return { label: "Sanction : avertir", help: "La personne visée reçoit un avertissement. Le dossier est clos." };
    case "BANNED":
      return { label: "Sanction : bannir le compte", help: "Le compte visé est banni immédiatement et ses sessions sont coupées.", danger: true };
    case "DISMISSED":
      return { label: "Aucune sanction — classer", help: "Le signalement ne justifie pas de sanction. Le dossier est clos." };
    case "RESOLVED":
      return { label: "Marquer comme traité", help: "La demande a été traitée. L'utilisateur en est informé." };
    case "PLANNED":
      return { label: "Marquer comme planifié", help: "C'est prévu, mais pas encore fait. L'utilisateur en est informé." };
    case "DECLINED":
      return { label: "Refuser la demande", help: "Vous ne donnerez pas suite. L'utilisateur en est informé." };
    case "REOPENED":
      return { label: "Revenir en arrière", help: "Le dossier repart dans la file d'attente pour être réexaminé." };
    default:
      return { label: kind, help: "" };
  }
}

/** Historique du dossier : un événement technique → une phrase lisible. */
function eventLabel(kind?: string): string {
  switch (kind) {
    case "RECEIVED": return "Signalement reçu";
    case "IN_REVIEW": return "Analyse commencée";
    case "NEEDS_INFO": return "Complément demandé";
    case "ACTIONED": return "Traité (action prise)";
    case "WARNED": return "Avertissement envoyé";
    case "BANNED": return "Compte banni";
    case "DISMISSED": return "Classé sans suite";
    case "REOPENED": return "Dossier rouvert";
    case "ASSIGNED": return "Dossier attribué";
    case "NOTE": return "Note interne ajoutée";
    case "VAULT_ACCESSED": return "Preuves consultées";
    case "VAULT_PURGED": return "Preuves effacées (délai écoulé)";
    case "ADMIN_MESSAGE": return "Message envoyé à l'utilisateur";
    case "ADMIN_EMAIL": return "E-mail envoyé à l'utilisateur";
    case "USER_REPLY": return "Réponse de l'utilisateur";
    case "RESOLVED": return "Marqué comme traité";
    case "PLANNED": return "Marqué comme planifié";
    case "DECLINED": return "Demande refusée";
    default: return kind ?? "Événement";
  }
}

/**
 * Longueurs minimales EXIGÉES PAR LE SERVEUR (protocole) — dupliquées ici pour
 * pouvoir prévenir AVANT l'envoi. Le serveur reste l'autorité ; ces valeurs ne
 * servent qu'à éviter un aller-retour qui reviendrait en « Invalid body ».
 */
const MIN_PUBLIC_REASON = 5; // CreateReportEventSchema.publicReason
const MIN_SANCTION_REASON = 3; // BanUserSchema/BanDeviceSchema.reason

/**
 * Traduit ce que renvoie l'API en une phrase compréhensible par un modérateur.
 * Un écran de modération ne doit JAMAIS afficher « Invalid body » ou « 400 » :
 * la personne en face décide d'un bannissement, elle a besoin de savoir quoi
 * corriger. (Défaut relevé par Henri en usage réel, 2 août.)
 */
function humanError(e: unknown, fallback: string): string {
  const raw = e instanceof Error ? e.message : "";
  if (/INVALID_BODY|Invalid body|Invalid data|INVALID_DATA/i.test(raw)) {
    return `Le texte saisi ne convient pas : ${MIN_PUBLIC_REASON} caractères minimum pour un message, ${MIN_SANCTION_REASON} pour un motif de sanction.`;
  }
  if (/NO_TARGET/.test(raw)) return "Ce compte a été clôturé : il n'est plus joignable.";
  if (/CANNOT_BAN_ADMIN/.test(raw)) return "Ce compte est administrateur : il ne peut pas être banni.";
  if (/TARGET_NOT_FOUND/.test(raw)) return "Le compte visé est introuvable.";
  if (/INVALID_TRANSITION/.test(raw)) {
    return "Cette décision n'est plus possible : le dossier a changé d'état entre-temps (il vient d'être rechargé).";
  }
  if (/EMAIL_FAILED/.test(raw)) return "L'e-mail n'est pas parti. Rien n'a été enregistré.";
  if (/FEATURE_DISABLED/.test(raw)) return "Cette fonctionnalité est désactivée sur le serveur.";
  return raw && !/^\d{3}$/.test(raw) ? raw : fallback;
}

const TYPE_LABEL: Record<string, string> = {
  REPORT_CONTENT: "Signalement",
  BUG: "Bug",
  FEATURE: "Suggestion",
  SUPPORT: "Demande d'aide",
};

export function ReportDetail({
  reportId,
  onBack,
  onAuthLost,
}: {
  reportId: string;
  onBack: () => void;
  onAuthLost: () => void;
}) {
  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [vault, setVault] = useState<Vault | null>(null);
  const [vaultBusy, setVaultBusy] = useState(false);

  const [note, setNote] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);

  // Bandeau d'avancement : le panneau de choix ne s'ouvre qu'à la demande, pour
  // qu'une décision ne soit jamais à un clic de distance par accident.
  const [stepOpen, setStepOpen] = useState(false);
  const [stepReason, setStepReason] = useState("");
  const [stepBusy, setStepBusy] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  // Retour d'Henri (2 août) : « on ne voit pas bien ce que l'on a choisi ».
  // On SÉLECTIONNE d'abord (l'option choisie devient visible), on confirme
  // ensuite — au lieu de déclencher la décision au premier clic.
  const [stepChoice, setStepChoice] = useState<string | null>(null);

  const [adminMessage, setAdminMessage] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);
  // MOD-2 — le modérateur CHOISIT son canal (arbitrage d'Henri) : message dans
  // l'application, ou e-mail. Le texte saisi est le même ; seul le tuyau change.
  const [channel, setChannel] = useState<"app" | "email">("app");
  const [msgDone, setMsgDone] = useState<string | null>(null);
  const [windowBusy, setWindowBusy] = useState(false);

  // Encart sanction — motif SÉPARÉ du motif public : celui-ci part à la personne
  // sanctionnée, mot pour mot.
  const [sanctionReason, setSanctionReason] = useState("");
  const [sanctionBusy, setSanctionBusy] = useState(false);
  const [sanctionError, setSanctionError] = useState<string | null>(null);
  const [sanctionDone, setSanctionDone] = useState<string | null>(null);
  const [pickedDevices, setPickedDevices] = useState<string[]>([]);
  const [bannedDeviceIds, setBannedDeviceIds] = useState<Set<string>>(new Set());

  const handleAuthError = useCallback(
    (e: unknown): boolean => {
      if (e instanceof AuthError && e.status === 401) {
        onAuthLost();
        return true;
      }
      return false;
    },
    [onAuthLost]
  );

  const load = useCallback(() => {
    setError(null);
    getJson<Dossier>(`/v1/admin/reports/${reportId}`)
      .then(setDossier)
      .catch((e) => {
        if (!handleAuthError(e)) setError(e instanceof Error ? e.message : "Erreur");
      });
  }, [reportId, handleAuthError]);

  useEffect(load, [load]);

  // Appareils déjà bannis — pour ne pas proposer deux fois la même sanction.
  // Best-effort : si la liste n'est pas lisible, l'écran reste utilisable.
  useEffect(() => {
    getJson<{ devices?: Array<{ deviceId: string }> }>(`/v1/admin/banned`)
      .then((b) => setBannedDeviceIds(new Set((b.devices ?? []).map((d) => d.deviceId))))
      .catch(() => {});
  }, [reportId]);

  async function openVault() {
    setVaultBusy(true);
    try {
      setVault(await getJson<Vault>(`/v1/admin/reports/${reportId}/vault`));
      load(); // l'ouverture ajoute un événement « Preuves consultées »
    } catch (e) {
      if (!handleAuthError(e)) setError(e instanceof Error ? e.message : "Coffre illisible");
    } finally {
      setVaultBusy(false);
    }
  }

  async function addNote() {
    if (!note.trim()) return;
    setNoteBusy(true);
    try {
      await postJson(`/v1/admin/reports/${reportId}/events`, { kind: "NOTE", internalNote: note.trim() });
      setNote("");
      load();
    } catch (e) {
      if (!handleAuthError(e)) setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setNoteBusy(false);
    }
  }

  /**
   * Fait avancer (ou reculer) le dossier.
   *
   * Le serveur exige un motif d'AU MOINS 5 CARACTÈRES et répond « Invalid body »
   * sinon — un message que personne ne peut comprendre (signalé par Henri en
   * usage réel). On valide donc AVANT d'envoyer, avec une phrase qui dit quoi
   * faire, et on traduit ce qui remonte quand même.
   */
  async function applyStep(kind: string) {
    const reason = stepReason.trim();
    if (reason.length < MIN_PUBLIC_REASON) {
      setStepError(
        reason.length === 0
          ? "Écrivez d'abord le message destiné à l'utilisateur."
          : `Le message est trop court : ${MIN_PUBLIC_REASON} caractères minimum (actuellement ${reason.length}).`
      );
      return;
    }
    setStepError(null);
    setStepBusy(kind);
    try {
      await postJson(`/v1/admin/reports/${reportId}/events`, { kind, publicReason: reason });
      setStepReason("");
      setStepChoice(null);
      setStepOpen(false);
      load();
    } catch (e) {
      if (handleAuthError(e)) return;
      setStepError(humanError(e, "Action refusée."));
      load();
    } finally {
      setStepBusy(null);
    }
  }

  async function sendMessage() {
    if (!adminMessage.trim()) return;
    setMsgError(null);
    setMsgDone(null);
    setMsgBusy(true);
    try {
      if (channel === "email") {
        await postJson(`/v1/admin/reports/${reportId}/email`, { message: adminMessage.trim() });
        setMsgDone("E-mail envoyé.");
      } else {
        await postJson(`/v1/admin/reports/${reportId}/events`, {
          kind: "ADMIN_MESSAGE",
          publicReason: adminMessage.trim(),
        });
        setMsgDone("Message envoyé dans l'application.");
      }
      setAdminMessage("");
      load();
    } catch (e) {
      if (handleAuthError(e)) return;
      const msg = e instanceof Error ? e.message : "Envoi impossible";
      // L'API distingue les deux causes qu'un modérateur peut comprendre.
      setMsgError(
        /NO_TARGET/.test(msg)
          ? "Ce compte a été clôturé : il n'est plus joignable."
          : /EMAIL_FAILED/.test(msg)
            ? "L'e-mail n'est pas parti (problème d'envoi). Rien n'a été enregistré."
            : msg
      );
    } finally {
      setMsgBusy(false);
    }
  }

  /** Sanctions directes : compte et/ou appareils. */
  async function runSanction(target: "account" | "devices" | "both", userId?: string) {
    const reason = sanctionReason.trim();
    if (reason.length < MIN_SANCTION_REASON) {
      setSanctionError(
        reason.length === 0
          ? "Un motif est obligatoire — il sera lu par la personne sanctionnée."
          : `Motif trop court : ${MIN_SANCTION_REASON} caractères minimum.`
      );
      return;
    }
    if ((target === "devices" || target === "both") && pickedDevices.length === 0) {
      setSanctionError("Sélectionnez au moins un appareil.");
      return;
    }
    setSanctionError(null);
    setSanctionDone(null);
    setSanctionBusy(true);
    try {
      const done: string[] = [];
      if ((target === "account" || target === "both") && userId) {
        await postJson(`/v1/admin/users/${userId}/ban`, { reason });
        done.push("compte banni");
      }
      if (target === "devices" || target === "both") {
        for (const d of pickedDevices) {
          await postJson(`/v1/admin/devices/${encodeURIComponent(d)}/ban`, { reason });
        }
        setBannedDeviceIds((prev) => new Set([...prev, ...pickedDevices]));
        done.push(`${pickedDevices.length} appareil(s) bloqué(s)`);
        setPickedDevices([]);
      }
      setSanctionDone(done.join(" · "));
      setSanctionReason("");
      load();
    } catch (e) {
      if (handleAuthError(e)) return;
      setSanctionError(humanError(e, "Sanction refusée."));
    } finally {
      setSanctionBusy(false);
    }
  }

  /** MOD-1 — ouvre ou referme le droit de réponse du déclarant. */
  async function setReplyWindow(open: boolean) {
    setWindowBusy(true);
    setMsgError(null);
    try {
      await postJson(`/v1/admin/reports/${reportId}/reply-window`, { open });
      setMsgDone(open ? "L'utilisateur peut maintenant vous répondre." : "L'utilisateur ne peut plus répondre.");
      load();
    } catch (e) {
      if (!handleAuthError(e)) setMsgError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setWindowBusy(false);
    }
  }

  async function liftAccountBan(userId: string) {
    setSanctionBusy(true);
    setSanctionError(null);
    try {
      await postJson(`/v1/admin/users/${userId}/unban`);
      setSanctionDone("bannissement levé");
      load();
    } catch (e) {
      if (!handleAuthError(e)) setSanctionError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setSanctionBusy(false);
    }
  }

  if (error) return <ErrorBanner message={error} />;
  if (!dossier) return <Spinner label="Ouverture du dossier…" />;

  const type = (dossier.type as ReportType) ?? "REPORT_CONTENT";
  const status = ((dossier.productStatus as ReportProductStatus) ||
    reportProductStatus(dossier.status ?? "OPEN")) as ReportProductStatus;
  const allowed = allowedReportTransitions(status, type);
  const forward = allowed.filter((k) => k !== "REOPENED");
  const canGoBack = allowed.includes("REOPENED");
  const events = dossier.events ?? [];
  const target = dossier.reportedUser ?? null;
  const devices = target?.devices ?? [];
  const closed = isTerminalStatus(status);

  // Les mots du déclarant : le motif d'origine + ceux des dépôts complémentaires.
  const reporterWords = [
    dossier.reason ? { text: dossier.reason, at: dossier.createdAt } : null,
    ...events.filter((e) => e.reporterReason).map((e) => ({ text: e.reporterReason as string, at: e.createdAt })),
  ].filter(Boolean) as Array<{ text: string; at?: string }>;

  return (
    <div>
      <button
        onClick={onBack}
        style={{ background: "transparent", border: "none", color: theme.ink3, fontSize: 13, marginBottom: 12 }}
      >
        ← Retour à la liste des dossiers
      </button>

      {/* ─── En-tête ────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <h1 style={{ fontFamily: fontStack.uiSemiBold, fontSize: 21 }}>
          Dossier {dossier.reference || dossier.id.slice(0, 8)}
        </h1>
        <Badge>{TYPE_LABEL[type] ?? type}</Badge>
        {target?.isBanned && <Badge tone="error">compte banni</Badge>}
        {closed && <Badge tone="accent">dossier clos</Badge>}
      </div>

      {/* ─── Bandeau : avancement + pilotage (demande d'Henri) ──────────── */}
      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 420px", minWidth: 280 }}>
            <StatusPath steps={buildPath(status, type)} />
          </div>
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            {canGoBack && (
              <Button
                variant="ghost"
                disabled={stepBusy !== null}
                onClick={() => {
                  setStepOpen(true);
                  setStepError(null);
                }}
                title="Remettre le dossier dans la file pour le réexaminer"
              >
                ← Étape précédente
              </Button>
            )}
            <Button
              disabled={forward.length === 0}
              onClick={() => {
                setStepOpen((v) => !v);
                setStepError(null);
              }}
            >
              {forward.length === 0 ? "Dossier terminé" : stepOpen ? "Fermer" : "Étape suivante →"}
            </Button>
          </div>
        </div>

        {stepOpen && (
          <div style={{ marginTop: 16, borderTop: `1px solid ${theme.line}`, paddingTop: 16 }}>
            {stepError && <div style={{ color: theme.error, fontSize: 13, marginBottom: 10 }}>{stepError}</div>}
            <div style={{ color: theme.ink2, fontSize: 13, marginBottom: 8 }}>
              1. Choisissez la décision
            </div>
            <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
              {(canGoBack ? [...forward, "REOPENED"] : forward).map((kind) => {
                const c = actionCopy(kind);
                const picked = stepChoice === kind;
                return (
                  <button
                    key={kind}
                    onClick={() => {
                      setStepChoice(picked ? null : kind);
                      setStepError(null);
                    }}
                    disabled={stepBusy !== null}
                    aria-pressed={picked}
                    style={{
                      textAlign: "left",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      // L'option choisie se VOIT : fond appuyé + liseré épais.
                      background: picked ? theme.surface3 : theme.surface2,
                      border: `${picked ? 2 : 1}px solid ${
                        picked ? (c.danger ? theme.error : theme.accent) : c.danger ? theme.error : theme.line
                      }`,
                      borderRadius: radius.field,
                      padding: picked ? "11px 13px" : "12px 14px",
                      cursor: "pointer",
                      opacity: stepBusy !== null ? 0.5 : 1,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        marginTop: 2,
                        width: 16,
                        height: 16,
                        flexShrink: 0,
                        borderRadius: 999,
                        border: `2px solid ${picked ? (c.danger ? theme.error : theme.accent) : theme.line}`,
                        background: picked ? (c.danger ? theme.error : theme.accent) : "transparent",
                      }}
                    />
                    <span>
                      <span
                        style={{
                          display: "block",
                          fontFamily: fontStack.uiSemiBold,
                          fontSize: 14.5,
                          color: c.danger ? theme.error : theme.ink,
                        }}
                      >
                        {c.label}
                      </span>
                      <span style={{ display: "block", fontSize: 12.5, color: theme.ink3, marginTop: 3 }}>
                        {c.help}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <label style={{ display: "block", marginBottom: 6 }}>
              <span style={{ display: "block", color: theme.ink2, fontSize: 13, marginBottom: 6 }}>
                2. Message à l'utilisateur — <b>il le verra</b> dans le suivi de son signalement
              </span>
              <textarea
                value={stepReason}
                onChange={(e) => {
                  setStepReason(e.target.value);
                  if (stepError) setStepError(null);
                }}
                rows={2}
                placeholder="Ex. : nous avons examiné votre signalement et pris les mesures nécessaires."
                style={inputStyle}
              />
            </label>
            {/* Exigence ANNONCÉE, plutôt qu'un « Invalid body » après coup. */}
            <div style={{ fontSize: 11.5, color: theme.ink3, marginBottom: 12 }}>
              {stepReason.trim().length === 0
                ? `${MIN_PUBLIC_REASON} caractères minimum.`
                : stepReason.trim().length < MIN_PUBLIC_REASON
                  ? `Encore ${MIN_PUBLIC_REASON - stepReason.trim().length} caractère(s).`
                  : `${stepReason.trim().length} caractères.`}
            </div>

            {stepError && <div style={{ color: theme.error, fontSize: 13, marginBottom: 10 }}>{stepError}</div>}

            {/* 3. Confirmation — on rappelle NOIR SUR BLANC ce qui va se passer. */}
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
                borderTop: `1px solid ${theme.line}`,
                paddingTop: 12,
              }}
            >
              <div style={{ flex: "1 1 240px", fontSize: 13, color: stepChoice ? theme.ink : theme.ink3 }}>
                {stepChoice ? (
                  <>
                    Décision choisie :{" "}
                    <b style={{ color: actionCopy(stepChoice).danger ? theme.error : theme.ink }}>
                      {actionCopy(stepChoice).label}
                    </b>
                  </>
                ) : (
                  "Aucune décision choisie."
                )}
              </div>
              <Button
                onClick={() => stepChoice && applyStep(stepChoice)}
                disabled={!stepChoice || stepBusy !== null}
                variant={stepChoice && actionCopy(stepChoice).danger ? "ghost" : "primary"}
              >
                {stepBusy !== null ? "…" : "Confirmer la décision"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* ─── Deux colonnes : travail au centre, outils à droite ─────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(300px, 1fr)", gap: 18, alignItems: "start" }}>
        {/* CENTRE — ordre demandé : message / historique / conversation / note */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Card>
            <SectionTitle>Message de l'utilisateur</SectionTitle>
            {reporterWords.length === 0 ? (
              <div style={{ color: theme.ink3 }}>Aucun message n'accompagne ce dossier.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {reporterWords.map((w, i) => (
                  <div key={i}>
                    <div
                      style={{
                        background: theme.surface2,
                        border: `1px solid ${theme.line}`,
                        borderRadius: radius.bubble,
                        padding: "12px 15px",
                        fontSize: 15,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {w.text}
                    </div>
                    <div style={{ fontSize: 11.5, color: theme.ink3, marginTop: 4 }}>
                      {i === 0 ? "Signalement initial" : "Ajout de l'utilisateur"} · {formatDateTime(w.at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle>Historique du dossier</SectionTitle>
            {events.length === 0 ? (
              <div style={{ color: theme.ink3 }}>Aucun événement.</div>
            ) : (
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
                {events.map((ev, i) => (
                  <li key={ev.id ?? i} style={{ borderLeft: `2px solid ${theme.line}`, paddingLeft: 14 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                      <span style={{ fontFamily: fontStack.uiSemiBold, fontSize: 14 }}>{eventLabel(ev.kind)}</span>
                      <span style={{ color: theme.ink3, fontSize: 12 }}>{formatDateTime(ev.createdAt)}</span>
                      {ev.actor?.displayName && (
                        <span style={{ color: theme.ink3, fontSize: 12 }}>· par {ev.actor.displayName}</span>
                      )}
                    </div>
                    {ev.publicReason && (
                      <div style={{ marginTop: 4, fontSize: 14 }}>{ev.publicReason}</div>
                    )}
                    {ev.internalNote && (
                      <div style={{ marginTop: 4, color: theme.ink2, fontSize: 13.5, fontStyle: "italic" }}>
                        Note interne : {ev.internalNote}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <SectionTitle>Conversation signalée</SectionTitle>
            {dossier.hasVault === false ? (
              <div style={{ color: theme.ink3 }}>
                Aucune conversation n'a été enregistrée pour ce dossier.
              </div>
            ) : vault ? (
              <>
                <div style={{ color: theme.ink3, fontSize: 12.5, marginBottom: 14 }}>
                  {vault.turnCount ?? vault.entries?.length ?? 0} message(s) figé(s) le{" "}
                  {formatDateTime(vault.capturedAt ?? undefined)}
                  {vault.purgeAfter ? ` · effacement automatique le ${formatDate(vault.purgeAfter)}` : ""}
                </div>
                <Transcript
                  entries={vault.entries ?? []}
                  media={vault.media}
                  reportedUserId={dossier.reportedUserId}
                  reporterId={dossier.reporterId}
                />
              </>
            ) : (
              <>
                <p style={{ color: theme.ink3, fontSize: 13.5, marginBottom: 12, lineHeight: 1.5 }}>
                  Les messages échangés ont été mis de côté comme preuve. Les afficher est
                  <b> tracé dans l'historique</b> : ouvrez-les seulement si vous en avez besoin pour décider.
                </p>
                <Button onClick={openVault} disabled={vaultBusy}>
                  {vaultBusy ? "…" : "Afficher la conversation"}
                </Button>
              </>
            )}
          </Card>

          <Card>
            <SectionTitle>Note interne</SectionTitle>
            <p style={{ color: theme.ink3, fontSize: 13, marginBottom: 10 }}>
              Visible uniquement par l'équipe. L'utilisateur ne la verra jamais.
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ce que vous voulez retenir pour la suite…"
              rows={3}
              style={inputStyle}
            />
            <div style={{ marginTop: 10 }}>
              <Button onClick={addNote} disabled={noteBusy || !note.trim()}>
                {noteBusy ? "…" : "Enregistrer la note"}
              </Button>
            </div>
          </Card>
        </div>

        {/* DROITE — ordre demandé : contacter / coffre / sanction */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Card>
            <SectionTitle>Contacter l'utilisateur</SectionTitle>

            {/* MOD-2 — choix du canal. Deux boutons plutôt qu'un menu : le
                modérateur voit d'un coup d'œil ce qui va se passer. */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {(
                [
                  { key: "app" as const, label: "Dans l'application" },
                  { key: "email" as const, label: "Par e-mail" },
                ]
              ).map((c) => (
                <button
                  key={c.key}
                  onClick={() => {
                    setChannel(c.key);
                    setMsgError(null);
                    setMsgDone(null);
                  }}
                  style={{
                    flex: 1,
                    background: channel === c.key ? theme.surface3 : "transparent",
                    color: channel === c.key ? theme.ink : theme.ink3,
                    border: `1px solid ${channel === c.key ? theme.accent : theme.line}`,
                    borderRadius: radius.pill,
                    padding: "7px 10px",
                    fontSize: 13,
                    fontFamily: channel === c.key ? fontStack.uiSemiBold : fontStack.ui,
                    cursor: "pointer",
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <p style={{ color: theme.ink3, fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
              {channel === "app"
                ? "Votre message apparaît dans le suivi de son signalement, et il reçoit une notification."
                : "Un e-mail lui est envoyé dans sa langue, avec la référence du dossier. Il peut y répondre directement."}
            </p>

            {msgError && <div style={{ color: theme.error, fontSize: 13, marginBottom: 10 }}>{msgError}</div>}
            {msgDone && <div style={{ color: theme.accent, fontSize: 13, marginBottom: 10 }}>✓ {msgDone}</div>}
            <textarea
              value={adminMessage}
              onChange={(e) => setAdminMessage(e.target.value)}
              placeholder={
                channel === "app"
                  ? "Votre message…"
                  : "Ex. : pouvez-vous nous préciser la date des faits ?"
              }
              rows={3}
              style={inputStyle}
            />
            <div style={{ marginTop: 10 }}>
              <Button onClick={sendMessage} disabled={msgBusy || !adminMessage.trim()}>
                {msgBusy ? "…" : channel === "app" ? "Envoyer dans l'application" : "Envoyer l'e-mail"}
              </Button>
            </div>
            {/* MOD-1 — la porte de réponse. Fermée par défaut : un dossier
                tranché ne doit pas rester une boîte de réception ouverte. */}
            <div style={{ marginTop: 14, borderTop: `1px solid ${theme.line}`, paddingTop: 12 }}>
              <div style={{ fontSize: 13, color: theme.ink2, marginBottom: 6 }}>
                {dossier.replyOpen
                  ? "L'utilisateur peut vous répondre depuis l'application."
                  : "L'utilisateur ne peut pas vous répondre."}
              </div>
              <Button
                variant="ghost"
                disabled={windowBusy}
                onClick={() => setReplyWindow(!dossier.replyOpen)}
                style={{ width: "100%" }}
                title={
                  dossier.replyOpen
                    ? "Ferme le fil : ses réponses seront refusées"
                    : "Ouvre le fil : il pourra écrire dans le suivi de son signalement"
                }
              >
                {windowBusy
                  ? "…"
                  : dossier.replyOpen
                    ? "Fermer les réponses"
                    : "Autoriser l'utilisateur à répondre"}
              </Button>
              <p style={{ color: theme.ink3, fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>
                Ses réponses arrivent dans l'historique, et vous recevez un e-mail.
              </p>
            </div>
          </Card>

          {/* Replié par défaut : on l'ouvre quand on en a besoin (retour d'Henri
              du 3 août — l'écran montrait tout, tout le temps). */}
          <Collapsible
            title="Coffre de preuves"
            hint={
              dossier.hasVault === false
                ? "Rien n'a été mis de côté"
                : vault
                  ? "Ouvert — conversation affichée au centre"
                  : "Fermé — consultation tracée"
            }
          >
            {dossier.hasVault === false ? (
              <div style={{ color: theme.ink3, fontSize: 13.5 }}>Rien n'a été mis de côté pour ce dossier.</div>
            ) : (
              <div style={{ fontSize: 13.5, color: theme.ink2, display: "flex", flexDirection: "column", gap: 8 }}>
                <div>
                  {vault
                    ? "Ouvert — la conversation est affichée au centre."
                    : "Fermé — ouvrez-le depuis « Conversation signalée »."}
                </div>
                {vault?.reporterMeta && (
                  <div style={{ color: theme.ink3, fontSize: 12.5, lineHeight: 1.6 }}>
                    Origine du signalement :{" "}
                    {vault.reporterMeta.platform || "plateforme inconnue"}
                    {vault.reporterMeta.deviceModel ? ` · ${vault.reporterMeta.deviceModel}` : ""}
                    {vault.reporterMeta.ip ? ` · ${vault.reporterMeta.ip}` : ""}
                  </div>
                )}
                {(dossier.vaultAccesses?.length ?? 0) > 0 && (
                  <div style={{ color: theme.ink3, fontSize: 12.5 }}>
                    Consulté {dossier.vaultAccesses!.length} fois — dernier accès{" "}
                    {formatDateTime(dossier.vaultAccesses![dossier.vaultAccesses!.length - 1]?.at)}
                  </div>
                )}
              </div>
            )}
          </Collapsible>

          {/* Replié aussi : bannir n'est ni le geste le plus fréquent, ni celui
              qu'on veut voir en permanence sous les yeux. Le résumé dit l'essentiel. */}
          <Collapsible
            title="Sanction"
            accent={target?.isBanned === true}
            hint={
              target?.isBanned
                ? `${target.displayName || "Le compte"} est banni`
                : target
                  ? `${target.displayName || "Compte visé"} — aucune sanction`
                  : "Aucun compte visé"
            }
          >

            {!target && !dossier.group && (
              <div style={{ color: theme.ink3, fontSize: 13.5 }}>
                Ce dossier ne vise aucun compte en particulier.
              </div>
            )}

            {target && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 14, marginBottom: 2 }}>
                  <b>{target.displayName || "Compte visé"}</b>
                </div>
                <div style={{ color: theme.ink3, fontSize: 12.5 }}>
                  {(dossier.targetReportCount ?? 0) > 1
                    ? `${dossier.targetReportCount} signalements reçus au total`
                    : "1er signalement reçu"}
                </div>
                {target.isBanned && (
                  <div
                    style={{
                      marginTop: 10,
                      background: "rgba(255,107,94,0.12)",
                      border: `1px solid ${theme.error}`,
                      borderRadius: radius.field,
                      padding: "10px 12px",
                      fontSize: 13,
                    }}
                  >
                    Compte banni le {formatDate(target.bannedAt ?? undefined)}
                    {target.banReason ? ` — « ${target.banReason} »` : ""}
                    {/* MOD-4 — dire ce que le bannissement empêche VRAIMENT. */}
                    <div style={{ color: theme.ink2, fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                      Son adresse e-mail est bloquée : elle ne peut pas recréer de compte
                      avec. Lever le bannissement la débloque aussi.
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Button variant="ghost" disabled={sanctionBusy} onClick={() => liftAccountBan(target.id)}>
                        Lever le bannissement
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {(target || devices.length > 0) && !target?.isBanned && (
              <>
                {sanctionError && (
                  <div style={{ color: theme.error, fontSize: 13, marginBottom: 10 }}>{sanctionError}</div>
                )}
                {sanctionDone && (
                  <div style={{ color: theme.accent, fontSize: 13, marginBottom: 10 }}>✓ {sanctionDone}</div>
                )}

                <label style={{ display: "block", marginBottom: 12 }}>
                  <span style={{ display: "block", color: theme.ink2, fontSize: 13, marginBottom: 6 }}>
                    Motif de la sanction
                  </span>
                  <textarea
                    value={sanctionReason}
                    onChange={(e) => setSanctionReason(e.target.value)}
                    rows={2}
                    placeholder="Ex. : propos menaçants envers un autre membre."
                    style={inputStyle}
                  />
                  {/* Ce texte part MOT POUR MOT à la personne bannie (auth.ts, 403 du
                      login et du refresh). Le dire ici est la seule chose qui empêche
                      un modérateur d'y écrire une note interne — et donc d'exposer le
                      nom du déclarant à la personne sanctionnée. */}
                  <span
                    style={{
                      display: "block",
                      marginTop: 6,
                      fontSize: 12,
                      color: theme.warning,
                      lineHeight: 1.5,
                    }}
                  >
                    ⚠️ Ce texte <b>sera lu par la personne sanctionnée</b> quand elle tentera de
                    se connecter. N'y écrivez <b>aucune note interne</b> et ne nommez personne.
                  </span>
                </label>

                {devices.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ color: theme.ink2, fontSize: 13, marginBottom: 6 }}>
                      Appareils utilisés par ce compte
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {devices.map((d) => {
                        const already = bannedDeviceIds.has(d.deviceId);
                        return (
                          <label
                            key={d.deviceId}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              fontSize: 13,
                              color: already ? theme.ink3 : theme.ink,
                            }}
                          >
                            <input
                              type="checkbox"
                              disabled={already}
                              checked={pickedDevices.includes(d.deviceId)}
                              onChange={(e) =>
                                setPickedDevices((prev) =>
                                  e.target.checked
                                    ? [...prev, d.deviceId]
                                    : prev.filter((x) => x !== d.deviceId)
                                )
                              }
                            />
                            <span style={{ fontFamily: fontStack.uiSemiBold, fontSize: 13 }}>
                              {deviceLabel(d.platform).name}
                            </span>
                            <span style={{ fontFamily: fontStack.mono, fontSize: 11.5, color: theme.ink3 }}>
                              {d.deviceId.slice(0, 8)}…
                            </span>
                            <span style={{ color: theme.ink3, fontSize: 12 }}>
                              {already ? "· déjà bloqué" : `· ${deviceLabel(d.platform).note}`}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <p style={{ color: theme.ink3, fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>
                      Bloquer un navigateur n'empêche pas de revenir depuis un autre
                      navigateur : pour une personne déterminée, c'est le bannissement du
                      <b> compte</b> qui compte.
                    </p>
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {target && (
                    <Button
                      variant="ghost"
                      disabled={sanctionBusy}
                      onClick={() => runSanction("account", target.id)}
                      title="Bannit le compte, coupe ses sessions et bloque la réinscription avec la même adresse e-mail"
                    >
                      Bannir le compte
                    </Button>
                  )}
                  {devices.length > 0 && (
                    <Button
                      variant="ghost"
                      disabled={sanctionBusy || pickedDevices.length === 0}
                      onClick={() => runSanction("devices")}
                    >
                      Bloquer les appareils cochés
                    </Button>
                  )}
                  {target && devices.length > 0 && (
                    <Button
                      variant="ghost"
                      disabled={sanctionBusy || pickedDevices.length === 0}
                      onClick={() => runSanction("both", target.id)}
                    >
                      Bannir le compte ET les appareils cochés
                    </Button>
                  )}
                </div>
              </>
            )}

            {/* Signalement de groupe sans compte visé : sans cette liste, le
                dossier serait instatuable (défaut relevé en revue FE-4). */}
            {!target && dossier.group?.participants && dossier.group.participants.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ color: theme.ink2, fontSize: 13, marginBottom: 6 }}>
                  Membres du groupe « {dossier.group.name || "sans nom"} »
                </div>
                <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
                  {dossier.group.participants.map((p) => (
                    <li key={p.userId} style={{ fontSize: 13, color: theme.ink2 }}>
                      {p.user?.displayName || p.userId.slice(0, 8)}
                      {p.user?.isBanned ? " · déjà banni" : ""}
                    </li>
                  ))}
                </ul>
                <p style={{ color: theme.ink3, fontSize: 11.5, marginTop: 8 }}>
                  Pour sanctionner un membre, ouvrez un dossier le visant nommément.
                </p>
              </div>
            )}
          </Collapsible>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: theme.bg,
  color: theme.ink,
  border: `1px solid ${theme.line}`,
  borderRadius: radius.field,
  padding: "11px 13px",
  fontSize: 14,
  fontFamily: "inherit",
  resize: "vertical",
  outline: "none",
};

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: fontStack.uiSemiBold,
        fontSize: 13,
        color: theme.ink2,
        marginBottom: 12,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {children}
    </h2>
  );
}

function formatDateTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
}

function formatDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-FR", { dateStyle: "medium" });
}
